import React, { useState, useEffect, useMemo } from "react";
import {
  getMetricConfigForCategory,
  normalizeScoreValue,
} from "../utils/scoreMetrics.jsx";
import {
  Clock,
  MessageCircle,
  X,
  Star,
  Trophy,
  ThumbsUp,
  ThumbsDown,
  Info,
  Lightbulb,
  Activity,
} from "lucide-react";

const CoachingTimelineDashboard = ({ recording }) => {
  const [selectedMoment, setSelectedMoment] = useState(null);
  const [hoveredMoment, setHoveredMoment] = useState(null);
  const [selectedPositiveMoment, setSelectedPositiveMoment] = useState(null);
  const [hoveredPositiveMoment, setHoveredPositiveMoment] = useState(null);
  const [showAllInsights, setShowAllInsights] = useState(false);
  const [showAllReinforcements, setShowAllReinforcements] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [showInsightsInfo, setShowInsightsInfo] = useState(false);
  const [showReinforcementsInfo, setShowReinforcementsInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [realTimelineData, setRealTimelineData] = useState([]);
  const [realSkillMoments, setRealSkillMoments] = useState({});
  const [realPositiveMoments, setRealPositiveMoments] = useState({});
  const [realSkillsData, setRealSkillsData] = useState({});

  // Load real session data
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!recording) return;

        // AGGRESSIVE RESET: Clear all state when recording changes
        setSelectedMoment(null);
        setHoveredMoment(null);
        setSelectedPositiveMoment(null);
        setHoveredPositiveMoment(null);
        setShowAllInsights(false);
        setShowAllReinforcements(false);
        setFeedback({});
        setIsLoading(true);

        // VALIDATION: Ensure we have the correct recording data
        console.log({
          id: recording.recording_id,
          space: recording.space_name,
          hasTranscript: !!recording.transcript?.transcript,
          hasInsights: !!(
            recording.soft_skills_evaluation_2?.insights || recording.insights
          ),
          transcriptLength: recording.transcript?.transcript?.length || 0,
          insightsCount:
            (recording.soft_skills_evaluation_2?.insights || recording.insights)
              ?.length || 0,
        });

        // Build timeline from transcript
        const transcript = recording.transcript?.transcript;
        const userObjects = recording.user_objects;

        // Sort transcript by start time (only if transcript exists)
        const sortedTranscript = transcript
          ? [...transcript].sort((a, b) => a.start - b.start)
          : [];

        // Build timeline segments
        const timelineSegments = [];
        let currentTime = 0;

        for (let i = 0; i < sortedTranscript.length; i++) {
          const item = sortedTranscript[i];
          const user = userObjects[item.speakerId];

          // Skip segments without a valid speaker ID, user, or unknown speakers
          if (
            !item.speakerId ||
            !user ||
            !user.persona ||
            user.persona === "Unknown" ||
            !user.name
          ) {
            currentTime = item.end;
            continue;
          }

          // Add silence gap if there's one (30 second threshold)
          if (item.start > currentTime + 30) {
            timelineSegments.push({
              speaker: "silence",
              startTime: currentTime,
              duration: item.start - currentTime,
              activity: "silence",
              userId: "silence",
              userName: "Silence",
              userPersona: "Silence",
            });
          }

          // Add speaking segment
          const duration = item.end - item.start;
          let speaker = "teacher";
          if (user.persona === "Student") {
            speaker = "student";
          } else if (user.persona === "Guest") {
            speaker = "teacher";
          }

          timelineSegments.push({
            speaker,
            startTime: item.start,
            duration,
            activity: "speaking",
            text: item.text,
            userId: item.speakerId,
            userName: user.name || "Unknown User",
            userPersona: user.persona || "Unknown",
          });

          currentTime = item.end;
        }

        setRealTimelineData(timelineSegments);

        // Build skill moments from insights
        const improvementMoments = {};
        const positiveMoments = {};

        const insightsSource =
          recording.soft_skills_evaluation_2?.insights || recording.insights;
        if (insightsSource) {
          const insights = insightsSource;

          const improvements = insights.filter(
            (i) => i._type === "IMPROVEMENT"
          );
          improvements.forEach((insight, index) => {
            const momentId = `improvement-${index}`;
            improvementMoments[momentId] = {
              skill: insight.skillCategory.replace(/_/g, "") + "Effectiveness",
              type: insight.skillCategory,
              timeStart: insight.occurrence,
              timeEnd: insight.occurrence + 5,
              title: insight.details.observation.substring(0, 50) + "...",
              observation: insight.details.observation,
              suggestion: insight.details.suggestion,
              impact:
                insight.impactScore > 0.7
                  ? "high"
                  : insight.impactScore > 0.5
                  ? "medium"
                  : "low",
              example: insight.details.example,
              reinforcement: "improvement",
              category: insight.skillCategory,
            };
          });

          const encouragements = insights.filter(
            (i) => i._type === "ENCOURAGEMENT"
          );
          const uniqueCelebrations = new Map();
          encouragements.forEach((insight) => {
            const key = insight.details.celebration.substring(0, 100);
            if (
              !uniqueCelebrations.has(key) ||
              uniqueCelebrations.get(key).impactScore < insight.impactScore
            ) {
              uniqueCelebrations.set(key, insight);
            }
          });

          Array.from(uniqueCelebrations.values()).forEach((insight, index) => {
            const momentId = `positive-${index}`;
            positiveMoments[momentId] = {
              skill: insight.skillCategory.replace(/_/g, "") + "Effectiveness",
              type: insight.skillCategory,
              timeStart: insight.occurrence,
              timeEnd: insight.occurrence + 5,
              title: insight.details.celebration.substring(0, 50) + "...",
              observation: insight.details.celebration,
              celebration: insight.details.celebration,
              impact:
                insight.impactScore > 0.7
                  ? "high"
                  : insight.impactScore > 0.5
                  ? "medium"
                  : "low",
              reinforcement: "positive",
              category: insight.skillCategory,
            };
          });
        }

        setRealSkillMoments(improvementMoments);
        setRealPositiveMoments(positiveMoments);

        // Build skills data from scoring; unknown categories use scoreMetrics fallbacks
        const scoring =
          recording.soft_skills_evaluation_2?.scoring ||
          recording.scoring ||
          [];
        const skillsData = {};

        scoring.forEach((scoreItem) => {
          if (!scoreItem?.skillCategory) {
            return;
          }
          const config = getMetricConfigForCategory(scoreItem.skillCategory);
          if (!config) {
            return;
          }
          const normalizedScore = normalizeScoreValue(scoreItem.score);
          if (normalizedScore === null) {
            return;
          }
          skillsData[config.key] = {
            score: normalizedScore,
            label: config.fullLabel || config.label,
            level: scoreItem.level || null,
            reason: scoreItem.reason || "No detailed feedback available.",
            color: config.color,
            icon: config.icon,
          };
        });

        console.log("Available scores:", Object.keys(skillsData));

        setRealSkillsData(skillsData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [recording]);

  // Handle feedback submission
  const handleFeedback = (momentId, feedbackType) => {
    setFeedback((prev) => ({
      ...prev,
      [momentId]: feedbackType,
    }));

    // Here you would typically send the feedback to your backend
    console.log(`Feedback for ${momentId}: ${feedbackType}`);
  };

  // Extract Vimeo video ID from streaming link
  const getVimeoVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  const formatSkillCategory = (category) => {
    if (!category || typeof category !== "string") {
      return "";
    }
    const cfg = getMetricConfigForCategory(category);
    return (
      cfg?.fullLabel ||
      cfg?.label ||
      category
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ")
    );
  };

  const getCategoryIcon = (category) => {
    const cfg = category ? getMetricConfigForCategory(category) : null;
    if (cfg?.icon) {
      return React.cloneElement(cfg.icon, {
        className: "w-4 h-4 text-white",
      });
    }
    return <Lightbulb className="w-4 h-4 text-white" />;
  };

  const getColor = (speaker) => {
    switch (speaker) {
      case "teacher":
        return "#EF4444";
      case "student":
        return "#3B82F6";
      case "silence":
        return "#9CA3AF";
      default:
        return "#9CA3AF";
    }
  };

  const getPersonaColor = (persona) => {
    // Blue for students and guests
    if (persona === "Student" || persona === "Guest") {
      return "#3B82F6";
    }
    // Red for teachers and admins
    return "#EF4444";
  };

  const getSkillColor = (skillId) => {
    const moment =
      currentSkillMoments[skillId] || currentPositiveMoments[skillId];
    if (!moment) return "#9CA3AF";

    const category = moment.category || moment.type;
    const preset = category ? getMetricConfigForCategory(category) : null;
    return preset?.color || "#9CA3AF";
  };

  const getImpactBadgeColor = (impact) => {
    switch (impact) {
      case "high":
        return "bg-red-100 text-red-800 border-red-200";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const currentTimelineData = realTimelineData;
  const currentSkillMoments = realSkillMoments;
  const currentPositiveMoments = realPositiveMoments;
  const currentSkillsData = realSkillsData;

  // Group data by individual users
  const userChunksMap = {};
  const silenceChunks = [];

  currentTimelineData.forEach((chunk) => {
    if (chunk.speaker === "silence") {
      silenceChunks.push(chunk);
    } else {
      if (!userChunksMap[chunk.userId]) {
        userChunksMap[chunk.userId] = {
          chunks: [],
          userName: chunk.userName,
          userPersona: chunk.userPersona,
          speaker: chunk.speaker,
        };
      }
      userChunksMap[chunk.userId].chunks.push(chunk);
    }
  });

  // Convert to array and sort by persona (students first, then teachers)
  const userRows = Object.entries(userChunksMap)
    .map(([userId, data]) => ({
      userId,
      ...data,
    }))
    .sort((a, b) => {
      // Students first, then teachers/others
      if (a.userPersona === "Student" && b.userPersona !== "Student") return -1;
      if (a.userPersona !== "Student" && b.userPersona === "Student") return 1;
      return a.userName.localeCompare(b.userName);
    });

  const sessionTimelineInsightLegendItems = useMemo(() => {
    const categories = new Set();
    const collect = (obj) => {
      Object.values(obj || {}).forEach((m) => {
        const cat = m?.category || m?.type;
        if (cat && typeof cat === "string") {
          categories.add(cat);
        }
      });
    };
    collect(currentSkillMoments);
    collect(currentPositiveMoments);

    return Array.from(categories)
      .map((category) => {
        const cfg = getMetricConfigForCategory(category);
        const momentId =
          Object.keys(currentSkillMoments).find(
            (id) =>
              (currentSkillMoments[id]?.category ||
                currentSkillMoments[id]?.type) === category
          ) ||
          Object.keys(currentPositiveMoments).find(
            (id) =>
              (currentPositiveMoments[id]?.category ||
                currentPositiveMoments[id]?.type) === category
          );
        const color =
          cfg?.color ||
          (momentId ? getSkillColor(momentId) : null) ||
          "#9CA3AF";
        return {
          category,
          label: cfg?.label || formatSkillCategory(category),
          color,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [currentSkillMoments, currentPositiveMoments, currentSkillsData]);

  // Generate time markers based on actual session length in minutes
  const maxTimeSeconds =
    currentTimelineData.length > 0
      ? Math.max(
          ...currentTimelineData.map(
            (chunk) => chunk.startTime + chunk.duration
          )
        )
      : 3600; // 60 minutes default
  const maxTime = maxTimeSeconds / 60; // Convert to minutes
  const timeMarkers = [];
  const stepSize = 5; // 5 minute intervals
  for (let i = 0; i <= maxTime; i += stepSize) {
    timeMarkers.push(i);
  }

  const formatDuration = (duration) => {
    const minutes = Math.floor(duration);
    const seconds = Math.round((duration - minutes) * 60);
    if (minutes > 0 && seconds > 0) {
      return `${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  const [showScoreInfo, setShowScoreInfo] = useState({});
  const [hoveredScoreInfo, setHoveredScoreInfo] = useState(null);
  const [hoveredInsightsInfo, setHoveredInsightsInfo] = useState(false);
  const [hoveredReinforcementsInfo, setHoveredReinforcementsInfo] =
    useState(false);

  const CircularChart = ({ skillKey, skillData }) => {
    const score = skillData.score;
    const radius = 35;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    return (
      <div className="flex flex-col items-center w-44">
        <div className="relative w-20 h-20">
          <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 80 80">
            <circle
              cx="40"
              cy="40"
              r={radius}
              stroke="#E5E7EB"
              strokeWidth="6"
              fill="none"
            />
            <circle
              cx="40"
              cy="40"
              r={radius}
              stroke={skillData.color}
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-700 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div
                className="text-lg font-bold"
                style={{ color: skillData.color }}
              >
                {score}
              </div>
              <div className="text-xs text-gray-500">/100</div>
            </div>
          </div>
        </div>
        <div className="mt-2 text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <div style={{ color: skillData.color }}>{skillData.icon}</div>
            <h4 className="font-medium text-sm text-gray-800">
              {skillData.label}
            </h4>
          </div>
          {skillData.level && (
            <span
              className="inline-block px-2 py-0.5 mb-1 rounded-full text-xs font-medium"
              style={{
                color: skillData.color,
                backgroundColor: `${skillData.color}1A`,
              }}
            >
              {skillData.level}
            </span>
          )}
          <div className="mt-2 px-3">
            <p className="text-sm text-gray-500 leading-relaxed">
              {skillData.reason}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const TimelineRow = ({ chunks, label, color }) => (
    <div className="mb-4">
      <div className="text-sm font-medium mb-2" style={{ color: color }}>
        {label}
      </div>
      <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden border">
        {chunks.map((chunk, index) => (
          <div
            key={index}
            className="absolute top-0 h-full transition-all duration-300 hover:brightness-110 cursor-pointer"
            style={{
              left: `${(chunk.startTime / 60 / maxTime) * 100}%`,
              width: `${(chunk.duration / 60 / maxTime) * 100}%`,
              backgroundColor: color,
            }}
            title={`${(chunk.startTime / 60).toFixed(1)}m - ${(
              (chunk.startTime + chunk.duration) /
              60
            ).toFixed(1)}m (${formatDuration(chunk.duration)}) - ${
              chunk.activity
            }`}
          />
        ))}
      </div>
    </div>
  );

  const InsightsRow = () => (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-[#1e5bb8]">
            💡 Soft Skills Evaluation
          </div>
          <button
            onMouseEnter={() => setHoveredInsightsInfo(true)}
            onMouseLeave={() => setHoveredInsightsInfo(false)}
            className="p-1 rounded-full hover:bg-blue-50 transition-colors group relative"
            title="Hover for detailed information"
          >
            <Info className="w-4 h-4 text-[#2f80ed] group-hover:text-[#1e5bb8]" />
            {hoveredInsightsInfo && (
              <div className="absolute top-8 left-0 z-50 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                <div className="text-sm text-gray-800">
                  <h4 className="font-semibold mb-2 text-[#1e5bb8]">
                    Soft Skills Evaluation
                  </h4>
                  <p className="mb-2">
                    AI-identified areas for improvement in teaching.
                  </p>
                  <p className="text-xs text-gray-600">
                    Click icons to see detailed coaching suggestions and provide
                    feedback.
                  </p>
                </div>
                <div className="absolute -top-2 left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white"></div>
              </div>
            )}
          </button>
        </div>
        <button
          onClick={() => {
            setShowAllInsights(!showAllInsights);
            setShowAllReinforcements(false);
            setSelectedMoment(null);
            setSelectedPositiveMoment(null);
            if (!showAllInsights) {
              setTimeout(() => {
                document
                  .getElementById("all-coaching-opportunities")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 100);
            }
          }}
          className="text-xs text-[#2f80ed] hover:text-[#1e5bb8] font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
        >
          View All
        </button>
      </div>
      <div className="relative h-12 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg overflow-hidden border-2 border-blue-200">
        {Object.entries(currentSkillMoments).map(([momentId, moment]) => (
          <div
            key={momentId}
            className="absolute top-1 bottom-1 cursor-pointer transition-all duration-200 rounded-lg border-2 border-white shadow-md flex items-center justify-center group"
            style={{
              left: `${(moment.timeStart / 60 / maxTime) * 100}%`,
              width: `${Math.max(
                ((moment.timeEnd - moment.timeStart) / 60 / maxTime) * 100,
                2
              )}%`,
              backgroundColor: getSkillColor(momentId),
            }}
            onClick={() => {
              setSelectedMoment(selectedMoment === momentId ? null : momentId);
              setSelectedPositiveMoment(null);
              setShowAllInsights(false);
              setShowAllReinforcements(false);
              if (selectedMoment !== momentId) {
                setTimeout(() => {
                  document
                    .getElementById("improvement-details")
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 100);
              }
            }}
            onMouseEnter={() => setHoveredMoment(momentId)}
            onMouseLeave={() => setHoveredMoment(null)}
          >
            <div className="relative">
              {hoveredMoment === momentId && (
                <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 w-80 z-50">
                  <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="p-1 rounded"
                        style={{ backgroundColor: getSkillColor(momentId) }}
                      >
                        {getCategoryIcon(moment.category)}
                      </div>
                      <span className="font-semibold">{moment.title}</span>
                    </div>
                    <p className="text-gray-300 mb-2">{moment.observation}</p>
                    <p className="text-blue-200">
                      💡 Click for detailed coaching
                    </p>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const PositiveReinforcementsRow = () => (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-green-700">
            🌟 Excellence Highlights
          </div>
          <button
            onMouseEnter={() => setHoveredReinforcementsInfo(true)}
            onMouseLeave={() => setHoveredReinforcementsInfo(false)}
            className="p-1 rounded-full hover:bg-green-50 transition-colors group relative"
            title="Hover for detailed information"
          >
            <Info className="w-4 h-4 text-green-500 group-hover:text-green-700" />
            {hoveredReinforcementsInfo && (
              <div className="absolute top-8 left-0 z-50 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                <div className="text-sm text-gray-800">
                  <h4 className="font-semibold mb-2 text-green-700">
                    Excellence Highlights
                  </h4>
                  <p className="mb-2">
                    Moments demonstrating excellent soft skills and teaching
                    practices.
                  </p>
                  <p className="text-xs text-gray-600">
                    Click star icons to see celebrations and provide accuracy
                    feedback.
                  </p>
                </div>
                <div className="absolute -top-2 left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white"></div>
              </div>
            )}
          </button>
        </div>
        <button
          onClick={() => {
            setShowAllReinforcements(!showAllReinforcements);
            setShowAllInsights(false);
            setSelectedMoment(null);
            setSelectedPositiveMoment(null);
            if (!showAllReinforcements) {
              setTimeout(() => {
                document
                  .getElementById("all-excellence-highlights")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 100);
            }
          }}
          className="text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1 rounded hover:bg-green-50 transition-colors"
        >
          View All
        </button>
      </div>
      <div className="relative h-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg overflow-hidden border-2 border-green-200">
        {Object.entries(currentPositiveMoments).map(([momentId, moment]) => (
          <div
            key={momentId}
            className="absolute top-1 bottom-1 cursor-pointer transition-all duration-200 rounded-lg border-2 border-white shadow-md flex items-center justify-center group"
            style={{
              left: `${(moment.timeStart / 60 / maxTime) * 100}%`,
              width: `${Math.max(
                ((moment.timeEnd - moment.timeStart) / 60 / maxTime) * 100,
                2
              )}%`,
              backgroundColor: getSkillColor(momentId),
            }}
            onClick={() => {
              setSelectedPositiveMoment(
                selectedPositiveMoment === momentId ? null : momentId
              );
              setSelectedMoment(null);
              setShowAllInsights(false);
              setShowAllReinforcements(false);
              if (selectedPositiveMoment !== momentId) {
                setTimeout(() => {
                  document
                    .getElementById("excellence-details")
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 100);
              }
            }}
            onMouseEnter={() => setHoveredPositiveMoment(momentId)}
            onMouseLeave={() => setHoveredPositiveMoment(null)}
          >
            <div className="relative">
              {hoveredPositiveMoment === momentId && (
                <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 w-80 z-50">
                  <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="p-1 rounded"
                        style={{ backgroundColor: getSkillColor(momentId) }}
                      >
                        {getCategoryIcon(moment.category)}
                      </div>
                      <span className="font-semibold">{moment.title}</span>
                    </div>
                    <p className="text-gray-300 mb-2">{moment.observation}</p>
                    <p className="text-green-200">🌟 View excellence details</p>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const InsightsCardGrid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {Object.entries(currentSkillMoments).map(([momentId, moment]) => (
        <div
          key={momentId}
          className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 cursor-pointer group relative"
          onClick={() => {
            setSelectedMoment(selectedMoment === momentId ? null : momentId);
            setSelectedPositiveMoment(null);
            setShowAllInsights(false);
            setShowAllReinforcements(false);
          }}
        >
          {/* Minimalistic feedback buttons in top right */}
          <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFeedback(momentId, "positive");
              }}
              className={`p-1 rounded-full transition-colors ${
                feedback[momentId] === "positive"
                  ? "bg-green-100 text-green-600"
                  : "text-gray-400 hover:text-green-600 hover:bg-green-50"
              }`}
              title="Helpful"
            >
              <ThumbsUp className="w-2.5 h-2.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFeedback(momentId, "negative");
              }}
              className={`p-1 rounded-full transition-colors ${
                feedback[momentId] === "negative"
                  ? "bg-red-100 text-red-600"
                  : "text-gray-400 hover:text-red-600 hover:bg-red-50"
              }`}
              title="Not helpful"
            >
              <ThumbsDown className="w-2.5 h-2.5" />
            </button>
          </div>

          <div className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div
                className="p-2 rounded-lg flex-shrink-0"
                style={{
                  backgroundColor: getSkillColor(momentId),
                  color: "white",
                }}
              >
                {getCategoryIcon(moment.category)}
              </div>
              <div className="flex-1 pr-8">
                <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-gray-600">
                  {moment.title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>
                    {Math.floor(moment.timeStart / 60)}:
                    {String(Math.round(moment.timeStart % 60)).padStart(2, "0")}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs border ${getImpactBadgeColor(
                      moment.impact
                    )}`}
                  >
                    {moment.impact} impact
                  </span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {moment.observation}
            </p>

            <div className="flex items-center gap-2 text-sm">
              <MessageCircle className="w-4 h-4 text-[#2f80ed]" />
              <span className="font-medium text-gray-700">
                Click for coaching details
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const ReinforcementsCardGrid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {Object.entries(currentPositiveMoments).map(([momentId, moment]) => (
        <div
          key={momentId}
          className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 cursor-pointer group relative"
          onClick={() => {
            setSelectedPositiveMoment(
              selectedPositiveMoment === momentId ? null : momentId
            );
            setSelectedMoment(null);
            setShowAllInsights(false);
            setShowAllReinforcements(false);
          }}
        >
          {/* Minimalistic feedback buttons in top right */}
          <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFeedback(momentId, "positive");
              }}
              className={`p-1 rounded-full transition-colors ${
                feedback[momentId] === "positive"
                  ? "bg-green-100 text-green-600"
                  : "text-gray-400 hover:text-green-600 hover:bg-green-50"
              }`}
              title="Accurate"
            >
              <ThumbsUp className="w-2.5 h-2.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFeedback(momentId, "negative");
              }}
              className={`p-1 rounded-full transition-colors ${
                feedback[momentId] === "negative"
                  ? "bg-red-100 text-red-600"
                  : "text-gray-400 hover:text-red-600 hover:bg-red-50"
              }`}
              title="Not accurate"
            >
              <ThumbsDown className="w-2.5 h-2.5" />
            </button>
          </div>

          <div className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div
                className="p-2 rounded-lg flex-shrink-0"
                style={{
                  backgroundColor: getSkillColor(momentId),
                  color: "white",
                }}
              >
                {getCategoryIcon(moment.category)}
              </div>
              <div className="flex-1 pr-8">
                <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-gray-600">
                  {moment.title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>
                    {Math.floor(moment.timeStart / 60)}:
                    {String(Math.round(moment.timeStart % 60)).padStart(2, "0")}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs border ${getImpactBadgeColor(
                      moment.impact
                    )}`}
                  >
                    {moment.impact} impact
                  </span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {moment.observation}
            </p>

            <div className="flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-green-500" />
              <span className="font-medium text-gray-700">
                View excellence details
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex justify-around items-center">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full mb-2"></div>
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="space-y-6 font-sans"
      style={{ fontFamily: '"Source Sans 3", sans-serif' }}
    >
      {/* Session Header */}
      {recording && (
        <div
          className="bg-white rounded-2xl p-8 shadow-lg border-0"
          style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-2xl font-bold text-gray-900 mb-2"
                style={{ fontWeight: 700 }}
              >
                {recording.space_name}
              </h1>
              <p className="text-gray-600 mb-4">
                Session recorded on{" "}
                {new Date(
                  recording.recording_object.startingTime
                ).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500 mb-1">Duration</div>
              <div className="text-2xl font-semibold text-gray-900">
                {Math.floor(recording.recording_object.duration / 60)} min{" "}
                {recording.recording_object.duration % 60} sec
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Circular Skills Overview */}
      <div
        className="bg-white rounded-2xl p-8 shadow-lg border-0"
        style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
      >
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-8 items-start">
          {Object.entries(currentSkillsData).map(([key, data]) => (
            <CircularChart key={key} skillKey={key} skillData={data} />
          ))}
        </div>
      </div>

      {/* Session Smoothness Card */}
      {recording?.session_smoothness && (
        <div
          className="bg-white rounded-2xl p-6 shadow-lg border-0"
          style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
        >
          <div className="flex items-start gap-4">
            <div
              className={`p-3 rounded-xl ${
                recording.session_smoothness.smoothness === "HIGH"
                  ? "bg-green-100"
                  : recording.session_smoothness.smoothness === "MEDIUM"
                  ? "bg-amber-100"
                  : "bg-red-100"
              }`}
            >
              <Activity
                className={`w-6 h-6 ${
                  recording.session_smoothness.smoothness === "HIGH"
                    ? "text-green-600"
                    : recording.session_smoothness.smoothness === "MEDIUM"
                    ? "text-amber-600"
                    : "text-red-600"
                }`}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  Session Smoothness
                </h3>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    recording.session_smoothness.smoothness === "HIGH"
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : recording.session_smoothness.smoothness === "MEDIUM"
                      ? "bg-amber-100 text-amber-700 border border-amber-200"
                      : "bg-red-100 text-red-700 border border-red-200"
                  }`}
                >
                  {recording.session_smoothness.smoothness === "HIGH"
                    ? "High"
                    : recording.session_smoothness.smoothness === "MEDIUM"
                    ? "Medium"
                    : "Low"}
                </span>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {recording.session_smoothness.reason}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline with Dedicated Insights Row */}
      <div
        className="bg-white rounded-2xl p-8 shadow-lg border-0"
        style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
      >
        <h3
          className="text-xl font-semibold text-gray-900 mb-6"
          style={{ fontWeight: 600, color: "#1e293b" }}
        >
          Session Timeline
        </h3>

        {/* Time markers */}
        <div className="flex justify-between mb-4 text-xs text-gray-500">
          {timeMarkers.map((time) => (
            <div key={time} className="text-center">
              {time}m
            </div>
          ))}
        </div>

        {/* Timeline Rows - Individual Users */}
        {userRows.map((userRow) => (
          <TimelineRow
            key={userRow.userId}
            chunks={userRow.chunks}
            label={userRow.userName}
            color={getPersonaColor(userRow.userPersona)}
          />
        ))}
        <TimelineRow chunks={silenceChunks} label="Silence" color="#9CA3AF" />
        <InsightsRow />
        <PositiveReinforcementsRow />

        {/* Time scale bottom */}
        <div className="flex justify-between mt-2 mb-4 text-xs text-gray-500">
          {timeMarkers.map((time) => (
            <div key={time} className="w-px h-2 bg-gray-300"></div>
          ))}
        </div>

        {/* Legend: only insight categories present on this timeline */}
        {sessionTimelineInsightLegendItems.length > 0 && (
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 mb-6 border border-gray-200 shadow-sm">
            <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-4">
              {sessionTimelineInsightLegendItems.map((item) => (
                <div key={item.category} className="flex items-center gap-2">
                  <div
                    className="w-5 h-5 rounded-md shadow-md border border-white shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Insight Details */}
        {selectedMoment && currentSkillMoments[selectedMoment] && (
          <div
            id="improvement-details"
            className="bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-200 rounded-lg p-6 mb-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: getSkillColor(selectedMoment),
                      color: "white",
                    }}
                  >
                    {getCategoryIcon(
                      currentSkillMoments[selectedMoment].category
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="font-medium">
                        {Math.floor(
                          currentSkillMoments[selectedMoment].timeStart / 60
                        )}
                        :
                        {String(
                          Math.round(
                            currentSkillMoments[selectedMoment].timeStart % 60
                          )
                        ).padStart(2, "0")}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs border ${getImpactBadgeColor(
                          currentSkillMoments[selectedMoment].impact
                        )}`}
                      >
                        {currentSkillMoments[selectedMoment].impact} impact
                      </span>
                      <span className="text-sm font-medium">
                        {formatSkillCategory(
                          currentSkillMoments[selectedMoment].category ||
                            currentSkillMoments[selectedMoment].type ||
                            ""
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">
                      What We Observed:
                    </h5>
                    <p className="text-gray-600 bg-white rounded p-3 border">
                      {currentSkillMoments[selectedMoment].observation}
                    </p>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">
                      Coaching Suggestion:
                    </h5>
                    <p className="text-gray-600 bg-white rounded p-3 border">
                      {currentSkillMoments[selectedMoment].suggestion}
                    </p>
                  </div>

                  {currentSkillMoments[selectedMoment].example && (
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">
                        Try This Instead:
                      </h5>
                      <div className="bg-white rounded p-4 border border-green-200 bg-green-50">
                        <p className="italic text-gray-700">
                          {currentSkillMoments[selectedMoment].example}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Video Instance */}
                  <div className="border-t pt-4 mt-4">
                    <div className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between bg-gray-50 px-4 py-2">
                        <span className="text-sm font-medium text-gray-700">
                          {Math.floor(
                            currentSkillMoments[selectedMoment].timeStart / 60
                          )}
                          :
                          {String(
                            Math.floor(
                              currentSkillMoments[selectedMoment].timeStart % 60
                            )
                          ).padStart(2, "0")}
                        </span>
                      </div>
                      <iframe
                        key={`coaching-${selectedMoment}`}
                        src={`https://player.vimeo.com/video/${getVimeoVideoId(
                          recording?.recording_object?.streamingLink
                        )}?autoplay=1#t=${Math.floor(
                          currentSkillMoments[selectedMoment].timeStart
                        )}s`}
                        width="100%"
                        height="360"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Feedback Section */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() =>
                          handleFeedback(selectedMoment, "positive")
                        }
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                          feedback[selectedMoment] === "positive"
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-gray-50 text-gray-600 hover:bg-green-50 hover:text-green-700 border border-gray-200"
                        }`}
                      >
                        <ThumbsUp className="w-3 h-3" />
                        <span className="text-xs font-medium">Helpful</span>
                      </button>
                      <button
                        onClick={() =>
                          handleFeedback(selectedMoment, "negative")
                        }
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                          feedback[selectedMoment] === "negative"
                            ? "bg-red-100 text-red-700 border border-red-200"
                            : "bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-700 border border-gray-200"
                        }`}
                      >
                        <ThumbsDown className="w-3 h-3" />
                        <span className="text-xs font-medium">Not Helpful</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedMoment(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors ml-6 p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}

        {/* Selected Positive Reinforcement Details */}
        {selectedPositiveMoment &&
          currentPositiveMoments[selectedPositiveMoment] && (
            <div
              id="excellence-details"
              className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: getSkillColor(selectedPositiveMoment),
                        color: "white",
                      }}
                    >
                      {getCategoryIcon(
                        currentPositiveMoments[selectedPositiveMoment].category
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="font-medium">
                          {Math.floor(
                            currentPositiveMoments[selectedPositiveMoment]
                              .timeStart / 60
                          )}
                          :
                          {String(
                            Math.round(
                              currentPositiveMoments[selectedPositiveMoment]
                                .timeStart % 60
                            )
                          ).padStart(2, "0")}
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-xs border ${getImpactBadgeColor(
                            currentPositiveMoments[selectedPositiveMoment]
                              .impact
                          )}`}
                        >
                          {
                            currentPositiveMoments[selectedPositiveMoment]
                              .impact
                          }{" "}
                          impact
                        </span>
                        <span className="text-sm font-medium">
                          {formatSkillCategory(
                            currentPositiveMoments[selectedPositiveMoment]
                              .category ||
                              currentPositiveMoments[selectedPositiveMoment]
                                .type ||
                              ""
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-green-600" />
                        Celebration:
                      </h5>
                      <p className="text-gray-600 bg-white rounded p-3 border border-green-200">
                        {
                          currentPositiveMoments[selectedPositiveMoment]
                            .celebration
                        }
                      </p>
                    </div>

                    {currentPositiveMoments[selectedPositiveMoment].example && (
                      <div>
                        <h5 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Star className="w-4 h-4 text-blue-600" />
                          Example of Excellence:
                        </h5>
                        <div className="bg-white rounded p-4 border border-blue-200 bg-blue-50">
                          <p className="italic text-gray-700">
                            "
                            {
                              currentPositiveMoments[selectedPositiveMoment]
                                .example
                            }
                            "
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Video Instance */}
                    <div className="border-t pt-4 mt-4">
                      <div className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between bg-gray-50 px-4 py-2">
                          <span className="text-sm font-medium text-gray-700">
                            {Math.floor(
                              currentPositiveMoments[selectedPositiveMoment]
                                .timeStart / 60
                            )}
                            :
                            {String(
                              Math.floor(
                                currentPositiveMoments[selectedPositiveMoment]
                                  .timeStart % 60
                              )
                            ).padStart(2, "0")}
                          </span>
                        </div>
                        <iframe
                          key={`excellence-${selectedPositiveMoment}`}
                          src={`https://player.vimeo.com/video/${getVimeoVideoId(
                            recording?.recording_object?.streamingLink
                          )}?autoplay=1#t=${Math.floor(
                            currentPositiveMoments[selectedPositiveMoment]
                              .timeStart
                          )}s`}
                          width="100%"
                          height="360"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Feedback Section */}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() =>
                            handleFeedback(selectedPositiveMoment, "positive")
                          }
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                            feedback[selectedPositiveMoment] === "positive"
                              ? "bg-green-100 text-green-700 border border-green-200"
                              : "bg-gray-50 text-gray-600 hover:bg-green-50 hover:text-green-700 border border-gray-200"
                          }`}
                        >
                          <ThumbsUp className="w-3 h-3" />
                          <span className="text-xs font-medium">Accurate</span>
                        </button>
                        <button
                          onClick={() =>
                            handleFeedback(selectedPositiveMoment, "negative")
                          }
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                            feedback[selectedPositiveMoment] === "negative"
                              ? "bg-red-100 text-red-700 border border-red-200"
                              : "bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-700 border border-gray-200"
                          }`}
                        >
                          <ThumbsDown className="w-3 h-3" />
                          <span className="text-xs font-medium">
                            Not Accurate
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedPositiveMoment(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors ml-6 p-1"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
          )}
      </div>

      {/* Coaching Opportunities Card Grid */}
      {showAllInsights && (
        <div
          id="all-coaching-opportunities"
          className="bg-white rounded-2xl p-8 shadow-lg border-0"
          style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-xl font-semibold text-gray-900"
              style={{ fontWeight: 600, color: "#1e293b" }}
            >
              💡 All Soft Skills Evaluations
            </h3>
            <button
              onClick={() => setShowAllInsights(false)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium px-3 py-1 rounded hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
          <InsightsCardGrid />
        </div>
      )}

      {/* Excellence Highlights Card Grid */}
      {showAllReinforcements && (
        <div
          id="all-excellence-highlights"
          className="bg-white rounded-2xl p-8 shadow-lg border-0"
          style={{ boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-xl font-semibold text-gray-900"
              style={{ fontWeight: 600, color: "#1e293b" }}
            >
              🌟 All Excellence Highlights
            </h3>
            <button
              onClick={() => setShowAllReinforcements(false)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium px-3 py-1 rounded hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
          <ReinforcementsCardGrid />
        </div>
      )}
    </div>
  );
};

export default CoachingTimelineDashboard;
